import { useEffect, useCallback } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import Footer from '@/pages/home/components/Footer';
import { articlesContent } from '@/mocks/articleContent';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = slug ? articlesContent[slug] : undefined;

  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [slug]);

  useEffect(() => {
    if (article) {
      const siteUrl = import.meta.env.VITE_SITE_URL || window.location.origin;
      const articleUrl = `${siteUrl}/article/${slug}`;

      const articleSchema = {
        '@context': 'https://schema.org',
        '@type': 'Article',
        headline: article.title,
        description: article.description,
        datePublished: article.publishedDate,
        dateModified: article.modifiedDate,
        author: {
          '@type': 'Organization',
          name: 'Basetao Sheet',
          url: siteUrl,
        },
        publisher: {
          '@type': 'Organization',
          name: 'Basetao Sheet',
          url: siteUrl,
        },
        mainEntityOfPage: {
          '@type': 'WebPage',
          '@id': articleUrl,
        },
      };

      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.textContent = JSON.stringify(articleSchema);
      document.head.appendChild(script);

      return () => {
        document.head.removeChild(script);
      };
    }
  }, [article, slug]);

  const handleArticleClick = useCallback((e: MouseEvent) => {
    const target = e.target as HTMLElement;
    const anchor = target.closest('a');
    if (!anchor) return;

    const href = anchor.getAttribute('href');
    if (!href) return;

    if (href.startsWith('/') && !href.startsWith('//')) {
      e.preventDefault();
      navigate(href);
      window.scrollTo({ top: 0, behavior: 'instant' });
    }
  }, [navigate]);

  useEffect(() => {
    const container = document.querySelector('.article-content');
    if (!container) return;

    container.addEventListener('click', handleArticleClick);
    return () => container.removeEventListener('click', handleArticleClick);
  }, [handleArticleClick, article]);

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-heading font-bold text-foreground-900 mb-3">Article Not Found</h1>
          <p className="text-foreground-600 mb-6">The article you are looking for does not exist.</p>
          <Link
            to="/"
            className="whitespace-nowrap cursor-pointer inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-white font-semibold rounded-full text-sm hover:bg-primary-600 transition-all duration-200"
          >
            <i className="ri-arrow-left-line"></i>
            Back to Home
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background-50">
      <nav className="sticky top-0 z-40 bg-white border-b border-background-200">
        <div className="max-w-6xl mx-auto px-4 md:px-6 flex items-center justify-between h-14 md:h-16">
          <Link to="/" className="flex items-center gap-2 flex-shrink-0">
            <span className="text-lg md:text-xl font-heading font-bold text-foreground-900">
              Basetao Sheet
            </span>
          </Link>
          <div className="flex items-center gap-3 md:gap-5">
            <Link
              to="/"
              className="whitespace-nowrap text-sm font-medium text-foreground-800 hover:text-primary-600 transition-colors duration-150 cursor-pointer hidden sm:inline-flex"
            >
              Home
            </Link>
            <Link
              to="/article/ultimate-basetao-spreadsheet-guide"
              className="whitespace-nowrap text-sm font-medium text-foreground-800 hover:text-primary-600 transition-colors duration-150 cursor-pointer hidden sm:inline-flex"
            >
              Shopping Guide
            </Link>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="whitespace-nowrap cursor-pointer inline-flex items-center gap-1.5 px-4 py-2 bg-primary-500 text-white text-sm font-medium rounded-full hover:bg-primary-600 transition-all duration-200"
            >
              <i className="ri-external-link-line"></i>
              Shop Now
            </a>
          </div>
        </div>
      </nav>

      <main className="max-w-4xl mx-auto px-4 md:px-6 py-10 md:py-16">
        <article className="bg-white border border-background-200 rounded-lg p-6 md:p-10">
          <header className="mb-8 pb-6 border-b border-background-200">
            <div className="flex items-center gap-2 text-xs text-foreground-500 mb-3">
              <Link to="/" className="hover:text-primary-600 transition-colors cursor-pointer">
                Home
              </Link>
              <span>/</span>
              <span>Article</span>
            </div>
            <h1 className="text-2xl md:text-4xl font-heading font-bold text-foreground-900 mb-3 leading-tight">
              {article.title}
            </h1>
            <div className="flex flex-wrap items-center gap-4 text-xs text-foreground-500">
              <span className="flex items-center gap-1">
                <i className="ri-calendar-line"></i>
                Published: {article.publishedDate}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-time-line"></i>
                12 min read
              </span>
            </div>
          </header>

          <div
            className="article-content cursor-default"
            dangerouslySetInnerHTML={{ __html: article.content }}
          />

          <div className="mt-10 pt-6 border-t border-background-200">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div>
                <p className="text-sm text-foreground-600 mb-2">
                  Ready to start shopping?
                </p>
                <div className="flex flex-wrap gap-3">
                  <Link
                    to="/"
                    className="whitespace-nowrap cursor-pointer inline-flex items-center gap-2 px-5 py-2.5 bg-background-100 text-foreground-800 text-sm font-medium rounded-full hover:bg-background-200 transition-all duration-200 border border-background-200"
                  >
                    <i className="ri-arrow-left-line"></i>
                    Back to Home
                  </Link>
                  <a
                    href="https://kaspreadsheet.com"
                    target="_blank"
                    rel="noopener nofollow"
                    className="whitespace-nowrap cursor-pointer inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 text-white text-sm font-medium rounded-full hover:bg-primary-600 transition-all duration-200"
                  >
                    <i className="ri-shopping-bag-3-line"></i>
                    Visit Basetao Spreadsheet
                  </a>
                </div>
              </div>
            </div>
          </div>
        </article>
      </main>

      <Footer />
    </div>
  );
}